/**
 * Feature Discovery Hook
 *
 * Tracks which features users have discovered/seen and provides
 * contextual hints for first-time interactions.
 */

import { useState, useCallback, useEffect } from 'react';
import { StorageService } from '../services/storage';

// All discoverable features in the app
export type FeatureId =
  | 'add_position'
  | 'edit_position'
  | 'delete_position'
  | 'portfolio_filters'
  | 'portfolio_sort'
  | 'calculation_method'
  | 'dark_mode'
  | 'export_csv'
  | 'export_pdf'
  | 'tax_center'
  | 'keyboard_shortcuts'
  | 'swap_tracking'
  | 'price_alerts'
  | 'portfolio_snapshot'
  | 'ai_bot'
  | 'user_mode'
  | 'search_tokens'
  | 'compact_mode'
  | 'btc_comparison';

// Feature metadata for tooltips
export interface FeatureInfo {
  id: FeatureId;
  title: string;
  description: string;
  tips?: string[];
  learnMoreUrl?: string;
}

// Feature definitions with descriptions
export const FEATURE_INFO: Record<FeatureId, Omit<FeatureInfo, 'id'>> = {
  add_position: {
    title: 'Ajouter une position',
    description: 'Cliquez ici pour enregistrer un achat, un swap ou des revenus de staking/mining.',
    tips: [
      'Le coût de base est automatiquement calculé en FIFO',
      'Vous pouvez importer des transactions CSV',
    ],
  },
  edit_position: {
    title: 'Modifier une position',
    description: 'Cliquez sur une ligne pour voir les détails et modifier.',
  },
  delete_position: {
    title: 'Supprimer une position',
    description: 'Glissez vers la gauche ou utilisez le menu contextuel pour supprimer.',
  },
  portfolio_filters: {
    title: 'Filtres du portfolio',
    description: 'Filtrez par valeur minimale, recherche ou masquez les soldes nuls.',
    tips: [
      'Les filtres sont sauvegardés automatiquement',
    ],
  },
  portfolio_sort: {
    title: 'Trier le portfolio',
    description: 'Cliquez sur les en-têtes de colonnes pour trier par valeur, P&L, ou nom.',
  },
  calculation_method: {
    title: 'Méthode de calcul',
    description: 'Choisissez entre FIFO, LIFO ou Prix Moyen Pondéré pour le calcul des plus-values.',
    tips: [
      'FIFO est requis pour la déclaration fiscale française',
      'Le PMP est plus simple pour le suivi personnel',
    ],
  },
  dark_mode: {
    title: 'Mode sombre',
    description: 'Basculez entre le thème clair et sombre selon vos préférences.',
  },
  export_csv: {
    title: 'Export CSV',
    description: 'Exportez vos transactions au format CSV pour Excel ou votre comptable.',
  },
  export_pdf: {
    title: 'Export PDF fiscal',
    description: 'Générez un rapport PDF prêt pour votre déclaration fiscale.',
    tips: [
      'Inclut les plus-values réalisées et latentes',
      'Compatible avec le formulaire 2086',
    ],
  },
  tax_center: {
    title: 'Centre fiscal',
    description: 'Accédez à tous vos outils fiscaux : calculs, exports, stratégies.',
  },
  keyboard_shortcuts: {
    title: 'Raccourcis clavier',
    description: 'Utilisez Cmd/Ctrl + K pour la recherche rapide et autres raccourcis.',
    tips: [
      'Appuyez sur ? pour voir tous les raccourcis',
    ],
  },
  swap_tracking: {
    title: 'Suivi des swaps',
    description: 'Les échanges crypto-to-crypto sont automatiquement suivis avec le bon coût de base.',
  },
  price_alerts: {
    title: 'Alertes de prix',
    description: 'Créez des alertes pour être notifié quand un prix atteint votre objectif.',
  },
  portfolio_snapshot: {
    title: 'Snapshot du portfolio',
    description: 'Sauvegardez l\'état actuel de votre portfolio pour le comparer plus tard.',
  },
  ai_bot: {
    title: 'Bot IA',
    description: 'Recevez des analyses et signaux de trading basés sur l\'IA.',
    tips: [
      'Les signaux ne sont pas des conseils financiers',
    ],
  },
  user_mode: {
    title: 'Mode utilisateur',
    description: 'Basculez entre le mode Simple (essentiel) et Expert (toutes les fonctions).',
  },
  search_tokens: {
    title: 'Recherche de tokens',
    description: 'Tapez pour rechercher parmi plus de 10,000 cryptomonnaies.',
  },
  compact_mode: {
    title: 'Mode compact',
    description: 'Affichez plus de données à l\'écran avec une vue condensée.',
  },
  btc_comparison: {
    title: 'Comparaison BTC',
    description: 'Comparez la performance de votre portfolio par rapport au Bitcoin.',
  },
};

const STORAGE_KEY = 'discovered_features';

interface DiscoveredFeatures {
  [key: string]: {
    discoveredAt: string;
    interactionCount: number;
  };
}

/**
 * Hook for managing feature discovery state
 */
export function useFeatureDiscovery() {
  const [discoveredFeatures, setDiscoveredFeatures] = useState<DiscoveredFeatures>(() => {
    return StorageService.get<DiscoveredFeatures>(STORAGE_KEY, {});
  });

  // Save to storage when state changes
  useEffect(() => {
    StorageService.set(STORAGE_KEY, discoveredFeatures);
  }, [discoveredFeatures]);

  /**
   * Check if a feature has been discovered
   */
  const isFeatureDiscovered = useCallback((featureId: FeatureId): boolean => {
    return !!discoveredFeatures[featureId];
  }, [discoveredFeatures]);

  /**
   * Check if this is the first time seeing a feature
   */
  const isFirstTime = useCallback((featureId: FeatureId): boolean => {
    const feature = discoveredFeatures[featureId];
    return !feature || feature.interactionCount === 0;
  }, [discoveredFeatures]);

  /**
   * Mark a feature as discovered (user has seen the hint)
   */
  const markAsDiscovered = useCallback((featureId: FeatureId) => {
    setDiscoveredFeatures(prev => {
      if (prev[featureId]) {
        return {
          ...prev,
          [featureId]: {
            ...prev[featureId],
            interactionCount: prev[featureId].interactionCount + 1,
          },
        };
      }
      return {
        ...prev,
        [featureId]: {
          discoveredAt: new Date().toISOString(),
          interactionCount: 1,
        },
      };
    });
  }, []);

  /**
   * Get feature info with discovery state
   */
  const getFeatureInfo = useCallback((featureId: FeatureId): FeatureInfo & { isNew: boolean } => {
    const info = FEATURE_INFO[featureId];
    return {
      id: featureId,
      ...info,
      isNew: isFirstTime(featureId),
    };
  }, [isFirstTime]);

  /**
   * Get all undiscovered features (for progress tracking)
   */
  const getUndiscoveredFeatures = useCallback((): FeatureId[] => {
    return (Object.keys(FEATURE_INFO) as FeatureId[]).filter(
      id => !isFeatureDiscovered(id)
    );
  }, [isFeatureDiscovered]);

  /**
   * Get discovery progress percentage
   */
  const getDiscoveryProgress = useCallback((): number => {
    const total = Object.keys(FEATURE_INFO).length;
    const discovered = Object.keys(discoveredFeatures).length;
    return Math.round((discovered / total) * 100);
  }, [discoveredFeatures]);

  /**
   * Reset all discovered features (for testing)
   */
  const resetDiscovery = useCallback(() => {
    setDiscoveredFeatures({});
    StorageService.remove(STORAGE_KEY);
  }, []);

  /**
   * Check if user is new (no features discovered yet)
   */
  const isNewUser = useCallback((): boolean => {
    return Object.keys(discoveredFeatures).length === 0;
  }, [discoveredFeatures]);

  return {
    isFeatureDiscovered,
    isFirstTime,
    markAsDiscovered,
    getFeatureInfo,
    getUndiscoveredFeatures,
    getDiscoveryProgress,
    resetDiscovery,
    isNewUser,
    discoveredCount: Object.keys(discoveredFeatures).length,
    totalFeatures: Object.keys(FEATURE_INFO).length,
  };
}

export type UseFeatureDiscoveryReturn = ReturnType<typeof useFeatureDiscovery>;
